import React, { useMemo } from 'react'
import { Box, Typography, Skeleton, useTheme, useMediaQuery, IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { WIDGET_HEIGHT } from '../base-card/const'
import { ProgressDots } from 'src/Components/progress-bar'
import { I1Sedan, I1ElectricalChargingStation, J1RightarrowD } from '@e3dc-react/icons'
import { Link, useNavigate } from 'react-router-dom'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { DASHBOARD_WALLBOX_WIDGET_ID } from 'src/Libs/qa/ids'

const useStyles = makeStyles()(theme => ({
  container: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.effectStyles.shadowMain,
    backgroundColor: theme.palette.background.paper,
    minHeight: WIDGET_HEIGHT,
    minWidth: 200,
    alignItems: 'stretch',
    flex: '1 1 200px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: 'pointer',

    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      maxHeight: 'unset',
      minHeight: 'unset',
    },
  },

  subtitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,

    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
    },
  },
  topRowWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
  },
  iconStyle: {
    color: theme.palette.text.primary,
    display: 'none',

    [theme.breakpoints.up('sm')]: { display: 'block' },
  },
}))

export interface VehicleConnectingToWallboxSkeletonProps {
  id: string
  title?: string
  subtitle?: React.ReactNode
  wallboxIndex: number
  installationId?: string
  wallboxStorageId: number
  storageId?: string
}
const VehicleConnectingToWallboxSkeleton: React.FC<VehicleConnectingToWallboxSkeletonProps> = ({
  id,
  title,
  subtitle,
  wallboxIndex,
  installationId,
  wallboxStorageId,
  storageId,
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()

  const dotCount = upMd ? 7 : 5

  const link = useMemo(() => {
    const inServerView = storageId !== wallboxStorageId.toString()

    if (inServerView) {
      return `/installation/${installationId}/${storageId}/properties/wallbox/${wallboxStorageId}/${wallboxIndex}/${SystemSettingHashes.WallboxSettings}`
    }

    return `/installation/${installationId}/${wallboxStorageId}/properties/wallbox/${wallboxIndex}/${SystemSettingHashes.WallboxSettings}`
  }, [installationId, wallboxStorageId, storageId, wallboxIndex])

  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (link) {
      event.preventDefault()
      navigate(link)
    }
  }

  return (
    <Box className={classes.container} data-qa={`${DASHBOARD_WALLBOX_WIDGET_ID}-${id}`} onClick={handleClick}>
      <Box className={classes.topRowWrapper}>
        <Box display={'flex'} flexDirection={'column'} justifyContent={'center'}>
          <Typography variant="h3">{title}</Typography>
          <Typography variant="caption" className={classes.subtitleContainer}>
            {subtitle}
          </Typography>
        </Box>
        <Box className={classes.controls}>
          {link && (
            <Link to={link}>
              <IconButton className={classes.iconStyle}>
                <J1RightarrowD />
              </IconButton>
            </Link>
          )}
        </Box>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} marginTop="auto">
        <I1Sedan style={{ width: theme.spacing(6), height: theme.spacing(5) }} />
        <ProgressDots dotCount={dotCount} dotSize={6} />
        <I1ElectricalChargingStation style={{ width: theme.spacing(6), height: theme.spacing(5) }} />
      </Box>

      <Skeleton variant="rectangular" sx={{ marginTop: 'auto', borderRadius: 2, height: theme.spacing(6), width: '100%' }} />
    </Box>
  )
}

export default VehicleConnectingToWallboxSkeleton
